import { useState } from 'react';
import { CalendarPlus, Trash2 } from 'lucide-react';
import type { PaymentExamDay } from '../../types/payment';

// Ödeme ayarlarındaki deneme takvimi: "kapı yalnız deneme günleri" olan paket (Deneme Paketi)
// salonu yalnız bu günlerde, bütün gün açar. Tek tek ya da toplu (her cumartesi, ayın 2. ve 4. pazarı) eklenir.

type Props = {
  examDays: PaymentExamDay[];
  onChange: (examDays: PaymentExamDay[]) => void;
};

const INPUT_CLASS =
  'rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-violet-300 focus:bg-white focus:ring-4 focus:ring-violet-50';
const FIELD_LABEL_CLASS = 'mb-1 block text-xs font-semibold text-slate-500';

// Tarih metni ("2026-11-07") ile saat dilimine takılmadan gün hesabı.
const toYmd = (date: Date) => date.toISOString().slice(0, 10);
const parseYmd = (ymd: string) => new Date(`${ymd}T12:00:00Z`);
const todayYmd = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);

function formatDay(ymd: string): string {
  return parseYmd(ymd).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long', timeZone: 'UTC' });
}

function mergeDays(current: PaymentExamDay[], added: PaymentExamDay[]): PaymentExamDay[] {
  const byDate = new Map(current.map((item) => [item.date, item]));
  for (const item of added) if (!byDate.has(item.date)) byDate.set(item.date, item);
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

// Başlangıç–bitiş arasındaki cumartesiler ve/veya ayın 2. ve 4. pazarları.
function generateDays(start: string, end: string, saturday: string | null, sunday: string | null): PaymentExamDay[] {
  if (!start || !end || start > end) return [];
  const days: PaymentExamDay[] = [];
  for (let cursor = parseYmd(start); toYmd(cursor) <= end; cursor = new Date(cursor.getTime() + 86400000)) {
    const weekday = cursor.getUTCDay();
    if (saturday !== null && weekday === 6) days.push({ date: toYmd(cursor), label: saturday });
    const sundayOrder = Math.floor((cursor.getUTCDate() - 1) / 7) + 1;
    if (sunday !== null && weekday === 0 && (sundayOrder === 2 || sundayOrder === 4)) days.push({ date: toYmd(cursor), label: sunday });
  }
  return days;
}

export default function PaymentExamDaysEditor({ examDays, onChange }: Props) {
  const [single, setSingle] = useState({ date: '', label: '' });
  const [bulk, setBulk] = useState({ start: '', end: '', saturdayOn: true, saturday: 'TYT', sundayOn: true, sunday: 'AYT' });
  const today = todayYmd();
  const upcoming = examDays.filter((item) => item.date >= today).length;

  const addSingle = () => {
    if (!single.date) return;
    onChange(mergeDays(examDays, [{ date: single.date, label: single.label.trim() }]));
    setSingle({ date: '', label: '' });
  };

  const addBulk = () => {
    const days = generateDays(bulk.start, bulk.end, bulk.saturdayOn ? bulk.saturday.trim() : null, bulk.sundayOn ? bulk.sunday.trim() : null);
    if (days.length) onChange(mergeDays(examDays, days));
  };

  const bulkCount = generateDays(bulk.start, bulk.end, bulk.saturdayOn ? '' : null, bulk.sundayOn ? '' : null)
    .filter((item) => !examDays.some((other) => other.date === item.date)).length;

  return (
    <div>
      <h2 className='font-semibold text-slate-900'>Deneme Takvimi</h2>
      <p className='mt-1 text-sm text-slate-500'>
        “Salon kapısı yalnız deneme günlerinde açık” seçilen paket, salonu yalnız bu günlerde ve o gün boyunca açar. Diğer günlerde kod
        istendiğinde kişiye sonraki deneme günü söylenir. {upcoming > 0 ? `Yaklaşan ${upcoming} deneme günü var.` : 'Yaklaşan deneme günü yok.'}
      </p>

      <div className='mt-4 grid gap-3 lg:grid-cols-2'>
        <div className='rounded-xl border border-slate-200 p-3'>
          <p className='text-xs font-bold text-slate-700'>Toplu ekle</p>
          <div className='mt-2 grid grid-cols-2 gap-2'>
            <label className='block'>
              <span className={FIELD_LABEL_CLASS}>Başlangıç</span>
              <input type='date' value={bulk.start} onChange={(event) => setBulk({ ...bulk, start: event.target.value })} className={`${INPUT_CLASS} w-full`} />
            </label>
            <label className='block'>
              <span className={FIELD_LABEL_CLASS}>Bitiş</span>
              <input type='date' value={bulk.end} onChange={(event) => setBulk({ ...bulk, end: event.target.value })} className={`${INPUT_CLASS} w-full`} />
            </label>
          </div>
          <div className='mt-2 flex flex-col gap-2'>
            <label className='flex items-center gap-2 text-xs text-slate-600'>
              <input type='checkbox' checked={bulk.saturdayOn} onChange={(event) => setBulk({ ...bulk, saturdayOn: event.target.checked })} className='h-4 w-4 accent-violet-600' />
              <span className='w-36 font-semibold'>Her cumartesi</span>
              <input value={bulk.saturday} maxLength={40} onChange={(event) => setBulk({ ...bulk, saturday: event.target.value })} className={`${INPUT_CLASS} min-w-0 flex-1 py-1 text-xs`} />
            </label>
            <label className='flex items-center gap-2 text-xs text-slate-600'>
              <input type='checkbox' checked={bulk.sundayOn} onChange={(event) => setBulk({ ...bulk, sundayOn: event.target.checked })} className='h-4 w-4 accent-violet-600' />
              <span className='w-36 font-semibold'>Ayın 2. ve 4. pazarı</span>
              <input value={bulk.sunday} maxLength={40} onChange={(event) => setBulk({ ...bulk, sunday: event.target.value })} className={`${INPUT_CLASS} min-w-0 flex-1 py-1 text-xs`} />
            </label>
          </div>
          <button
            type='button'
            onClick={addBulk}
            disabled={bulkCount === 0}
            className='mt-3 inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50'
          >
            <CalendarPlus className='h-3.5 w-3.5' />{bulkCount > 0 ? `${bulkCount} günü ekle` : 'Eklenecek gün yok'}
          </button>
        </div>

        <div className='rounded-xl border border-slate-200 p-3'>
          <p className='text-xs font-bold text-slate-700'>Tek gün ekle</p>
          <div className='mt-2 flex flex-wrap items-end gap-2'>
            <label className='block'>
              <span className={FIELD_LABEL_CLASS}>Tarih</span>
              <input type='date' value={single.date} onChange={(event) => setSingle({ ...single, date: event.target.value })} className={INPUT_CLASS} />
            </label>
            <label className='block min-w-0 flex-1'>
              <span className={FIELD_LABEL_CLASS}>Açıklama</span>
              <input value={single.label} maxLength={40} placeholder='Örn. TYT maratonu' onChange={(event) => setSingle({ ...single, label: event.target.value })} className={`${INPUT_CLASS} w-full`} />
            </label>
            <button
              type='button'
              onClick={addSingle}
              disabled={!single.date}
              className='inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50'
            >
              <CalendarPlus className='h-3.5 w-3.5' />Ekle
            </button>
          </div>
        </div>
      </div>

      <div className='mt-3'>
        {examDays.length === 0 ? (
          <p className='rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500'>Takvimde gün yok.</p>
        ) : (
          <ul className='grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3'>
            {examDays.map((item) => (
              <li
                key={item.date}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${item.date < today ? 'border-slate-100 bg-slate-50 text-slate-400' : 'border-slate-200 bg-white text-slate-700'}`}
              >
                <span className='w-36 shrink-0 font-semibold'>{formatDay(item.date)}</span>
                <input
                  value={item.label}
                  maxLength={40}
                  aria-label={`${formatDay(item.date)} açıklaması`}
                  onChange={(event) => onChange(examDays.map((other) => (other.date === item.date ? { ...other, label: event.target.value } : other)))}
                  className='min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 hover:border-slate-200 focus:border-violet-300 focus:outline-none'
                />
                <button
                  type='button'
                  title='Günü sil'
                  onClick={() => onChange(examDays.filter((other) => other.date !== item.date))}
                  className='rounded p-1 text-red-500 hover:bg-red-50'
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
